import { useLingui } from "@lingui/react";
import { useEffect, useMemo, useState } from "react";
import { LOADING_TEXT } from "../../utils/constants/i18n-strings";
import {
  useRegionalInstanceLoginMethods,
  useRegionalInstanceDefaultLoginMethod,
} from "../../lib/[services]/regional-instance";
import { LoginMethod } from "./LoginMethods/types";
import { Button, HStack, VStack } from "@wootingkb/component-library";
import { Trans } from "@lingui/react/macro";
import { isLoginMethod, LoginMethodComponentMap } from "./LoginMethods/util";
import { useRouter } from "next/router";

export function LocalizedLoginMethodContainer() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const defaultLoginMethod = useRegionalInstanceDefaultLoginMethod();
  const loginMethods = useRegionalInstanceLoginMethods();
  const [selectedMethod, setSelectedMethod] = useState<LoginMethod>();
  const { _: i18n } = useLingui();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const { loginMethod } = router.query;

    if (typeof loginMethod === "string" && isLoginMethod(loginMethod)) {
      setSelectedMethod(loginMethod);
    }
  }, [router]);

  // `selectedMethod` only holds an explicit user/query choice. When there's no
  // valid explicit choice we fall back to the region's default — which stays
  // reactive to the resolved regional instance, so the default is applied
  // correctly after hydration and on every reload.
  const activeMethod =
    selectedMethod && loginMethods.includes(selectedMethod)
      ? selectedMethod
      : defaultLoginMethod;

  const additionalMethodsToDisplay = useMemo(
    () => loginMethods.filter((method) => method !== activeMethod),
    [loginMethods, activeMethod]
  );
  const SelectedLoginMethodComponent =
    LoginMethodComponentMap[activeMethod].component;

  if (loading) {
    return <div className="loader">{i18n(LOADING_TEXT)}</div>;
  }

  return (
    <VStack className="gap-12">
      <SelectedLoginMethodComponent setLoading={setLoading} />
      {additionalMethodsToDisplay.length ? (
        <VStack className="gap-4">
          <HStack role="separator" className="items-center gap-3">
            <span className="h-px flex-1 bg-black/10 dark:bg-white/10" />
            <span className="text-sm text-black/50 dark:text-white/50">
              <Trans id="Login_choice_or">or</Trans>
            </span>
            <span className="h-px flex-1 bg-black/10 dark:bg-white/10" />
          </HStack>
          {additionalMethodsToDisplay.map((method, index) => (
            <Button
              key={index}
              variant="outline"
              className="w-full"
              onClick={() => setSelectedMethod(method)}
            >
              {i18n(LoginMethodComponentMap[method].displayName)}
            </Button>
          ))}
        </VStack>
      ) : (
        <></>
      )}
    </VStack>
  );
}
